import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { calculateBMI, getBMIBadgeClasses } from '../utils/bmiCalculator';
import { Badge } from './ui/badge';
import MedicationIcon from './MedicationIcon';

// html2pdf is loaded via CDN in public/index.html
// Access it from window object
const html2pdf = window.html2pdf;

const PrescriptionView = () => {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const babyId = searchParams.get('babyId');
  const hideHospitalBranding = searchParams.get('hideHospitalBranding') === 'true';
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [prescriptionData, setPrescriptionData] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [digitalSignature, setDigitalSignature] = useState(null);
  const [prescriptionBackground, setPrescriptionBackground] = useState(null);
  const [showHospitalBrandingInPrint, setShowHospitalBrandingInPrint] = useState(true);
  const [showDoctorInfoInPrint, setShowDoctorInfoInPrint] = useState(true);
  const [showHospitalBrandingInPdf, setShowHospitalBrandingInPdf] = useState(true);
  const prescriptionRef = useRef(null);

  useEffect(() => {
    const fetchPrescriptionData = async () => {
      try {
        setLoading(true);
        
        // Fetch baby data
        const babyRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/babies/${babyId}`);
        
        // Fetch all medical sessions for this baby
        const sessionsRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/medical-sessions/${babyId}`);
        
        // Find the specific session
        const session = sessionsRes.data.find(s => s.id === sessionId);
        
        if (!session) {
          setError('Prescription not found');
          return;
        }
        
        // Fetch doctor's digital signature and prescription background if doctor_id is available
        let doctorSignature = null;
        let doctorPrescriptionBackground = null;
        if (session.updated_by) {
          try {
            const doctorRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/doctors/${session.updated_by}`);
            doctorSignature = doctorRes.data.digital_signature_url || null;
            doctorPrescriptionBackground = doctorRes.data.prescription_background_pdf || null;
            
            // Fetch hospital settings if doctor has hospital_id
            if (doctorRes.data.hospital_id) {
              try {
                const settingsRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/hospitals/${doctorRes.data.hospital_id}/settings`);
                if (settingsRes.data) {
                  if (settingsRes.data.show_hospital_branding_in_print !== undefined) {
                    setShowHospitalBrandingInPrint(settingsRes.data.show_hospital_branding_in_print);
                  }
                  if (settingsRes.data.show_doctor_info_in_print !== undefined) {
                    setShowDoctorInfoInPrint(settingsRes.data.show_doctor_info_in_print);
                  }
                  if (settingsRes.data.show_hospital_branding_in_pdf !== undefined) {
                    setShowHospitalBrandingInPdf(settingsRes.data.show_hospital_branding_in_pdf);
                  }
                }
              } catch (err) {
                console.log('Could not fetch hospital settings:', err);
              }
            }
          } catch (err) {
            console.log('Could not fetch doctor signature:', err);
          }
        }
        setDigitalSignature(doctorSignature);
        setPrescriptionBackground(doctorPrescriptionBackground);
        
        // Fetch medical background data (conditions, medications, allergies)
        const [conditionsRes, medicationsRes, allergiesRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/medical-conditions/${babyId}`).catch(() => ({ data: [] })),
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/medications/${babyId}`).catch(() => ({ data: [] })),
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/allergies/${babyId}`).catch(() => ({ data: [] }))
        ]);
        
        setPrescriptionData({
          baby: babyRes.data,
          session: session,
          medicalConditions: conditionsRes.data || [],
          medications: medicationsRes.data || [],
          allergies: allergiesRes.data || []
        });
      } catch (err) {
        console.error('Error fetching prescription data:', err);
        setError('Failed to load prescription');
      } finally {
        setLoading(false);
      }
    };

    if (babyId && sessionId) {
      fetchPrescriptionData();
    } else {
      setError('Invalid prescription link');
      setLoading(false);
    }
  }, [babyId, sessionId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading prescription...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-red-600 text-lg">{error}</p>
        </div>
      </div>
    );
  }

  const { baby, session } = prescriptionData;

  // Calculate age with detailed format: "7 years, 1 month, 20 days"
  const calculateAge = (dob) => {
    try {
      if (!dob) return 'N/A';
      
      let dobDate;
      if (dob.includes('/')) {
        const [day, month, year] = dob.split('/').map(s => parseInt(s, 10));
        dobDate = new Date(year, month - 1, day);
      } else if (dob.includes('-')) {
        const parts = dob.split('-').map(s => parseInt(s, 10));
        if (parts[0] > 1000) {
          dobDate = new Date(parts[0], parts[1] - 1, parts[2]);
        } else {
          dobDate = new Date(parts[2], parts[1] - 1, parts[0]);
        }
      } else {
        dobDate = new Date(dob);
      }
      
      if (isNaN(dobDate.getTime())) return 'N/A';
      
      const today = new Date();
      let years = today.getFullYear() - dobDate.getFullYear();
      let months = today.getMonth() - dobDate.getMonth();
      let days = today.getDate() - dobDate.getDate();
      
      // Adjust for negative days
      if (days < 0) {
        months--;
        const lastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
        days += lastMonth.getDate();
      }
      
      // Adjust for negative months
      if (months < 0) {
        years--;
        months += 12;
      }
      
      if (years < 0 || months < 0 || days < 0) return 'N/A';
      
      // Format output
      const parts = [];
      if (years > 0) parts.push(`${years} year${years !== 1 ? 's' : ''}`);
      if (months > 0) parts.push(`${months} month${months !== 1 ? 's' : ''}`);
      if (days > 0 || parts.length === 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`);
      
      return parts.join(', ');
    } catch (error) {
      console.error('Age calculation error:', error);
      return 'N/A';
    }
  };

  // Format date to DD/MM/YYYY
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    
    if (dateStr.includes('/')) {
      return dateStr;
    } else if (dateStr.includes('-')) {
      const parts = dateStr.split('-');
      if (parts[0].length === 4) {
        const [year, month, day] = parts;
        return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
      } else {
        return dateStr.replace(/-/g, '/');
      }
    }
    return dateStr;
  };

  // Get gender display
  const getGender = () => {
    const genderOrSex = baby.gender || baby.sex || '';
    const normalized = genderOrSex.toLowerCase();
    if (normalized === 'male' || normalized === 'm' || normalized === 'boy') return 'M';
    if (normalized === 'female' || normalized === 'f' || normalized === 'girl') return 'F';
    return genderOrSex?.[0]?.toUpperCase() || 'N/A';
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4 print:p-0 print:block print:min-h-0">
      <div ref={prescriptionRef} className="w-full bg-white rounded-lg shadow-lg print:shadow-none print:rounded-none print:max-w-none" style={{ maxWidth: '800px' }}>
        {/* Prescription Content */}
        <style>{`
          /* Print styles - fill full page with visible content */
          @media print {
            /* Reset body and page to prevent extra blank pages */
            body, html {
              margin: 0 !important;
              padding: 0 !important;
              width: 100% !important;
              height: auto !important;
            }
            
            /* Remove min-height from container to prevent extra pages */
            .print\:min-h-0 {
              min-height: 0 !important;
              height: auto !important;
            }
            
            /* Make prescription content visible and full width */
            .prescription-content {
              display: block !important;
              width: 100% !important;
              max-width: none !important;
              box-shadow: none !important;
              border-radius: 0 !important;
              margin: 0 !important;
              padding: 1rem !important;
              height: auto !important;
            }
            
            /* Ensure the main wrapper doesn't force extra pages */
            @page {
              margin: 0.5cm;
              size: A4;
            }
            
            /* Ensure all content is visible */
            .prescription-content * {
              visibility: visible !important;
            }
            
            /* Hide non-essential elements - buttons and non-printable content */
            .no-print,
            .no-print *,
            button.no-print,
            div.no-print {
              display: none !important;
              visibility: hidden !important;
              opacity: 0 !important;
              height: 0 !important;
              width: 0 !important;
              margin: 0 !important;
              padding: 0 !important;
            }

            /* Hide doctor info in header when toggle is off (only affects print, not PDF) */
            .hide-in-print {
              display: none !important;
              visibility: hidden !important;
            }
            
            /* Enhanced page break handling */
            
            /* Keep headers with their content - prevent orphaned headers */
            h1, h2, h3, h4, h5, h6, 
            .text-2xl, .text-xl, .text-lg, .font-semibold,
            .section-header {
              page-break-after: avoid !important;
              page-break-inside: avoid !important;
            }
            
            /* Keep header + immediate content together */
            .text-xl.font-semibold + * {
              page-break-before: avoid !important;
            }
            
            /* Section containers - keep header with content */
            .mb-6, .mb-8, .space-y-6 > div {
              page-break-inside: avoid !important;
            }
            
            /* Patient info section */
            .flex.justify-between.items-start {
              page-break-inside: avoid !important;
              page-break-after: avoid !important;
            }
            
            /* Medical background section */
            .bg-gray-50.p-4.rounded-lg {
              page-break-inside: avoid !important;
            }
            
            /* CRITICAL: Medication section - keep header with table */
            .medication-section {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            
            /* Force medication header to stay with table */
            .medication-header {
              page-break-after: avoid !important;
              break-after: avoid !important;
            }
            
            /* Ensure table starts immediately after header */
            .medication-header + .medication-table,
            .medication-header + table {
              page-break-before: avoid !important;
              break-before: avoid !important;
            }
            
            /* Alternative approach - treat as one block */
            .medication-section > * {
              page-break-before: avoid !important;
            }
            
            /* Specific medication table class */
            .medication-table {
              page-break-before: avoid !important;
              margin-top: 0 !important;
            }
            
            /* Table styles with better page break handling */
            table {
              page-break-inside: auto !important;
              break-inside: auto !important;
              page-break-after: auto !important;
              page-break-before: avoid !important;
              break-before: avoid !important;
              width: 100% !important;
              border-collapse: collapse !important;
              display: table !important;
            }
            
            /* Keep table headers visible on each page */
            thead {
              display: table-header-group !important;
            }
            
            /* Table rows */
            tr {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
              page-break-after: auto !important;
            }
            
            /* Prevent first row from being orphaned with header */
            tbody tr:first-child {
              page-break-before: avoid !important;
              break-before: avoid !important;
            }
            
            /* Table headers */
            th {
              font-weight: normal !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
              page-break-after: avoid !important;
              break-after: avoid !important;
            }
            
            /* Table cells */
            td, th {
              border: 1px solid #d1d5db !important;
              padding: 12px !important;
              display: table-cell !important;
            }
            
            /* Specific medication table improvements */
            table.medication-table {
              margin-top: 0px !important;
            }
            
            table.medication-table thead th {
              page-break-after: avoid !important;
              break-after: avoid !important;
            }
            
            /* Keep medication header with table - multiple approaches */
            .medication-header + table,
            .medication-header + .medication-table {
              page-break-before: avoid !important;
              break-before: avoid !important;
            }
            
            /* Ensure flex layouts work in print */
            .flex {
              display: flex !important;
            }
            
            .justify-between {
              justify-content: space-between !important;
            }
            
            .items-start {
              align-items: flex-start !important;
            }
            
            .items-center {
              align-items: center !important;
            }
            
            /* SVG/QR code visibility */
            svg {
              display: block !important;
            }
            
            /* Ensure images are visible in print */
            img {
              display: inline-block !important;
              visibility: visible !important;
              opacity: 1 !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            
            /* Ensure borders are visible in print */
            .border-b {
              border-bottom: 1px solid black !important;
            }
            
            /* Hide hospital branding in print when setting is disabled */
            ${!showHospitalBrandingInPrint ? `
            .hospital-branding-section {
              display: none !important;
              visibility: hidden !important;
              height: 0 !important;
              margin: 0 !important;
              padding: 0 !important;
            }
            ` : ''}
          }
          
          /* Screen-only: hide hospital branding if URL param is set */
          ${hideHospitalBranding ? `
          .hospital-branding-section {
            display: none !important;
          }
          ` : ''}
            
            .border-t {
              border-top: 1px solid black !important;
            }
            
            /* Signature section - keep together */
            .signature-section {
              page-break-inside: avoid !important;
              margin-top: 40px !important;
            }
            
            /* Footer - keep together */
            .text-center.text-sm.text-gray-600.mt-8 {
              page-break-inside: avoid !important;
              page-break-before: avoid !important;
            }
          }
        `}</style>

        <div className="prescription-content p-8 print:p-4">
          {/* Hospital and Doctor Information - Parallel Layout */}
          <div className="flex items-start mb-4">
            {/* Left Side - Hospital Logo, Name and Address */}
            <div className="flex items-center gap-3 flex-1 hospital-branding-section">
              {session.hospital_logo && (
                <img 
                  src={session.hospital_logo.startsWith('http') ? session.hospital_logo : `${process.env.REACT_APP_BACKEND_URL}${session.hospital_logo}`}
                  alt="Hospital logo" 
                  className="h-[100px] w-[100px] object-contain flex-shrink-0"
                  onError={(e) => {
                    console.error('Failed to load hospital logo:', session.hospital_logo);
                    e.target.style.display = 'none';
                  }}
                />
              )}
              <div>
                {session.hospital_name && (
                  <div className="text-lg font-bold text-gray-800">
                    {session.hospital_name}
                  </div>
                )}
                {session.hospital_address && (
                  <div className="text-sm text-gray-600 mt-0.5">
                    {session.hospital_address}
                  </div>
                )}
              </div>
            </div>

            {/* Right Side - Doctor Information */}
            <div className={`text-right ml-auto ${!showDoctorInfoInPrint ? 'hide-in-print' : ''}`}>
              <div className="text-base font-medium">
                {(() => {
                  // Parse doctor name to extract specialization
                  const doctorName = session.doctor_name || '';
                  const specializationMatch = doctorName.match(/\(([^)]+)\)$/);
                  const name = specializationMatch ? doctorName.replace(/\s*\([^)]+\)$/, '').trim() : doctorName;
                  return name;
                })()}
              </div>
              {(() => {
                const specializationMatch = (session.doctor_name || '').match(/\(([^)]+)\)$/);
                if (specializationMatch) {
                  return <div className="text-base text-gray-600 mt-1">{specializationMatch[1]}</div>;
                }
                return null;
              })()}
              {session.doctor_registration && (
                <div className="text-sm text-gray-600 mt-1">(Reg No: {session.doctor_registration})</div>
              )}
            </div>
          </div>

          {/* Line separator */}
          <div className="border-b border-gray-300 mb-4"></div>

          {/* Baby ID and Date - Parallel Layout */}
          <div className="flex justify-between items-start mb-4 pb-4 border-b border-gray-300">
            {/* Left Side - Baby Information */}
            <div className="flex-1">
              <div className="text-base font-medium mb-1 text-gray-700">
                {baby.short_id || baby.id?.substring(0, 8).toUpperCase()}
              </div>
              <div className="text-lg font-bold mb-1">
                {baby.name} ({getGender()})
              </div>
              <div className="text-base font-medium text-gray-800">
                Age: {calculateAge(baby.date_of_birth)}
              </div>
            </div>

            {/* Right Side - Date */}
            <div className="text-right ml-8">
              <div className="text-base text-gray-600">
                Date: {formatDate(session.session_date)}
              </div>
            </div>
          </div>

          {/* Prescription Symbol */}
          <div className="text-lg font-serif mb-4">℞</div>

          {/* Background Section */}
          {(prescriptionData.medicalConditions?.length > 0 || prescriptionData.medications?.length > 0 || prescriptionData.allergies?.length > 0) && (
            <div className="mb-4 border-b pb-3">
              <div className="text-base font-semibold mb-2">Background:</div>
              <div className="text-base space-y-1">
                {prescriptionData.medicalConditions && prescriptionData.medicalConditions.length > 0 && (
                  <div>
                    <span className="font-medium">Medical Conditions: </span>
                    <div className="ml-4 mt-1">
                      {prescriptionData.medicalConditions.map((c, idx) => (
                        <div key={idx}>
                          • {c.condition_name}
                          {c.medication && <span> - Medication: {c.medication}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {prescriptionData.medications && prescriptionData.medications.length > 0 && (
                  <div>
                    <span className="font-medium">Current Medications: </span>
                    <span>{prescriptionData.medications.map(m => m.medication_name).join(', ')}</span>
                  </div>
                )}
                {prescriptionData.allergies && prescriptionData.allergies.length > 0 && (
                  <div>
                    <span className="font-medium">Allergies: </span>
                    <span className="text-red-600 font-medium">{prescriptionData.allergies.map(a => a.allergen).join(', ')}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Vitals */}
          {(session.temperature || session.pulse || session.spo2 || session.blood_pressure || session.bp_systolic || session.bp_diastolic || session.height || session.weight) && (
            <div className="mb-4">
              <div className="text-base font-semibold mb-1">Vitals:</div>
              <div className="text-base font-medium space-y-1">
                <div>
                  {session.temperature && <span>Temp: {session.temperature}°F</span>}
                  {session.temperature && session.pulse && <span> | </span>}
                  {session.pulse && <span>Pulse: {session.pulse} bpm</span>}
                  {(session.pulse || session.temperature) && session.spo2 && <span> | </span>}
                  {session.spo2 && <span>SpO2: {session.spo2}%</span>}
                  {(session.spo2 || session.pulse || session.temperature) && (session.blood_pressure || (session.bp_systolic && session.bp_diastolic)) && <span> | </span>}
                  {session.blood_pressure ? <span>BP: {session.blood_pressure}</span> : 
                    (session.bp_systolic && session.bp_diastolic && <span>BP: {session.bp_systolic}/{session.bp_diastolic}</span>)}
                </div>
                <div>
                  {session.height && <span>Height: {session.height} cm</span>}
                  {session.height && session.weight && <span> | </span>}
                  {session.weight && <span>Weight: {session.weight} kg</span>}
                  {(() => {
                    if (session.height && session.weight && baby?.date_of_birth && baby?.gender) {
                      const bmiResult = calculateBMI(
                        parseFloat(session.weight),
                        parseFloat(session.height),
                        baby.date_of_birth,
                        baby.gender,
                        ''
                      );
                      if (bmiResult && bmiResult.bmi) {
                        return (
                          <>
                            <span> | </span>
                            <span>BMI: {bmiResult.bmi} ({bmiResult.status})</span>
                          </>
                        );
                      }
                    }
                    return null;
                  })()}
                </div>
              </div>
            </div>
          )}

          {/* Chief Complaints */}
          {session.complaints && (
            <div className="mb-4">
              <div className="text-xl font-semibold mb-1">Chief Complaints:</div>
              <div className="text-lg font-medium">{session.complaints}</div>
            </div>
          )}

          {/* Chief Complaints */}
          {(session.chief_complaints || session.symptoms) && (
            <div className="mb-4">
              <div className="text-base font-semibold mb-1 section-header">Chief Complaints:</div>
              <div className="text-base font-medium">{session.chief_complaints || session.symptoms}</div>
            </div>
          )}

          {/* Assessment */}
          {session.assessment && (
            <div className="mb-4">
              <div className="text-base font-semibold mb-1 section-header">Assessment:</div>
              <div className="text-base font-medium">{session.assessment}</div>
            </div>
          )}

          {/* Tests */}
          {session.tests && (
            <div className="mb-4">
              <div className="text-base font-semibold mb-1 section-header">Tests Recommended:</div>
              <div className="text-base font-medium">{session.tests}</div>
            </div>
          )}

          {/* Diagnosis */}
          {session.diagnosis && (
            <div className="mb-4">
              <div className="text-base font-semibold mb-1 section-header">Diagnosis:</div>
              <div className="text-base font-medium space-y-1">
                {session.diagnosis.split('\n').filter(line => line.trim()).map((line, idx) => (
                  <div key={idx}>{line}</div>
                ))}
              </div>
            </div>
          )}

          {/* Medication Table */}
          {session.medication && (
            <div className="mb-6 medication-section" style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
              <div className="text-base font-semibold mb-2 medication-header" style={{ pageBreakAfter: 'avoid', breakAfter: 'avoid' }}>Medications Prescribed:</div>
              <table className="w-full border-collapse border border-gray-300 medication-table" style={{ pageBreakBefore: 'avoid', breakBefore: 'avoid' }}>
                <thead>
                  <tr>
                    <th className="border border-gray-300 px-3 py-2 text-left text-base font-normal">#</th>
                    <th className="border border-gray-300 px-3 py-2 text-left text-base font-normal">Medicine Name</th>
                    <th className="border border-gray-300 px-3 py-2 text-left text-base font-normal">Dosage</th>
                    <th className="border border-gray-300 px-3 py-2 text-left text-base font-normal">Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {session.medication.split(' | ').map((med, index) => {
                    let name = med;
                    let medicationType = '';
                    let genericName = '';
                    let treatmentFor = '';
                    let frequency = '';
                    let dosage = '';
                    let duration = '';
                    
                    // Extract medication type if present
                    const typeMatch = name.match(/\[Type:\s*([^\]]+)\]/);
                    if (typeMatch) {
                      medicationType = typeMatch[1].trim();
                      name = name.replace(typeMatch[0], '').trim();
                    }
                    
                    // Extract generic name if present
                    const genericMatch = name.match(/\[Generic:\s*([^\]]+)\]/);
                    if (genericMatch) {
                      genericName = genericMatch[1].trim();
                      name = name.replace(genericMatch[0], '').trim();
                    }
                    
                    // Extract treatment for if present
                    const treatmentMatch = name.match(/\[For:\s*([^\]]+)\]/);
                    if (treatmentMatch) {
                      treatmentFor = treatmentMatch[1].trim();
                      name = name.replace(treatmentMatch[0], '').trim();
                    }
                    
                    // Extract frequency if present
                    const frequencyMatch = name.match(/\[Frequency:\s*([^\]]+)\]/);
                    if (frequencyMatch) {
                      frequency = frequencyMatch[1].trim();
                      name = name.replace(frequencyMatch[0], '').trim();
                    }
                    
                    const durationMatch = name.match(/for\s+(.+)$/i);
                    if (durationMatch) {
                      duration = durationMatch[1];
                      name = name.substring(0, durationMatch.index).trim();
                    }
                    
                    const timingMatch = name.match(/^(.+?)\s*\((.+?)\)(?:\s+-\s+(.+))?$/);
                    if (timingMatch) {
                      name = timingMatch[1];
                      const timing = timingMatch[2]; // Before Food / After Food
                      const timeOfDay = timingMatch[3] || ''; // Morning, Night, etc.
                      
                      // Parse selected times
                      const selectedTimes = timeOfDay ? timeOfDay.split(',').map(t => t.trim()) : [];
                      const isMorning = selectedTimes.includes('Morning');
                      const isAfternoon = selectedTimes.includes('Afternoon');
                      const isEvening = selectedTimes.includes('Evening');
                      const isNight = selectedTimes.includes('Night');
                      
                      // Create dosage with dots, timing, and frequency
                      dosage = {
                        times: { morning: isMorning, afternoon: isAfternoon, evening: isEvening, night: isNight },
                        timing: timing,
                        frequency: frequency
                      };
                    } else if (frequency) {
                      // If no time_of_day but frequency exists, show frequency
                      dosage = frequency;
                    }
                    
                    return (
                      <tr key={index}>
                        <td className="border border-gray-300 px-3 py-2 text-base font-medium">{index + 1}</td>
                        <td className="border border-gray-300 px-3 py-2">
                          <div className="flex items-center gap-2">
                            {medicationType && (
                              <MedicationIcon 
                                type={medicationType} 
                                size={24} 
                                className="text-gray-600 flex-shrink-0" 
                              />
                            )}
                            <div className="flex-1">
                              <div className="text-base font-medium leading-tight">{name}</div>
                              {(genericName || treatmentFor) && (
                                <div className="text-sm text-gray-600 mt-0.5">
                                  {genericName && <span className="italic">Generic: {genericName}</span>}
                                  {genericName && treatmentFor && <span className="mx-1">•</span>}
                                  {treatmentFor && <span>For: {treatmentFor}</span>}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="border border-gray-300 px-3 py-2">
                          {typeof dosage === 'object' && dosage.times ? (
                            <div className="flex flex-col items-center">
                              {/* Frequency if present - ABOVE dots */}
                              {dosage.frequency && (
                                <div className="text-base text-center mb-2 font-medium">{dosage.frequency}</div>
                              )}
                              {/* Dots and Labels - Each dot directly above its label */}
                              <div className="flex items-start justify-center gap-3">
                                {/* Morning */}
                                <div className="flex flex-col items-center gap-1">
                                  <span style={{ fontFamily: 'Arial, sans-serif', color: '#000', fontSize: '32px', lineHeight: '1' }}>
                                    {dosage.times.morning ? '●' : '○'}
                                  </span>
                                  <span className="text-xs font-semibold text-gray-900">M</span>
                                </div>
                                {/* Afternoon */}
                                <div className="flex flex-col items-center gap-1">
                                  <span style={{ fontFamily: 'Arial, sans-serif', color: '#000', fontSize: '32px', lineHeight: '1' }}>
                                    {dosage.times.afternoon ? '●' : '○'}
                                  </span>
                                  <span className="text-xs font-semibold text-gray-900">A</span>
                                </div>
                                {/* Evening */}
                                <div className="flex flex-col items-center gap-1">
                                  <span style={{ fontFamily: 'Arial, sans-serif', color: '#000', fontSize: '32px', lineHeight: '1' }}>
                                    {dosage.times.evening ? '●' : '○'}
                                  </span>
                                  <span className="text-xs font-semibold text-gray-900">E</span>
                                </div>
                                {/* Night */}
                                <div className="flex flex-col items-center gap-1">
                                  <span style={{ fontFamily: 'Arial, sans-serif', color: '#000', fontSize: '32px', lineHeight: '1' }}>
                                    {dosage.times.night ? '●' : '○'}
                                  </span>
                                  <span className="text-xs font-semibold text-gray-900">N</span>
                                </div>
                              </div>
                              {/* Timing (Before/After Food) */}
                              <div className="text-base text-center mt-2 font-medium">({dosage.timing})</div>
                            </div>
                          ) : (
                            <span className="text-base font-medium">{dosage}</span>
                          )}
                        </td>
                        <td className="border border-gray-300 px-3 py-2 text-base font-medium">{duration}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              
              {/* Doctor Signature Section - Only show if medications are prescribed */}
              <div className="mt-8 flex justify-end">
                <div className="text-right min-w-[200px]">
                  {/* Signature line */}
                  <div className="border-t-2 border-gray-800 mb-3"></div>
                  
                  {/* Doctor Name */}
                  <div className="text-base font-semibold">
                    {(() => {
                      // Parse doctor name to extract specialization
                      const doctorName = session.doctor_name || '';
                      const specializationMatch = doctorName.match(/\(([^)]+)\)$/);
                      const name = specializationMatch ? doctorName.replace(/\s*\([^)]+\)$/, '').trim() : doctorName;
                      return name;
                    })()}
                  </div>
                  
                  {/* Specialization */}
                  {(() => {
                    const specializationMatch = (session.doctor_name || '').match(/\(([^)]+)\)$/);
                    if (specializationMatch) {
                      return <div className="text-sm text-gray-600 mt-1">{specializationMatch[1]}</div>;
                    }
                    return null;
                  })()}
                  
                  {/* Registration Number */}
                  {session.doctor_registration && (
                    <div className="text-xs text-gray-600 mt-1">(Reg No: {session.doctor_registration})</div>
                  )}
                  
                  {/* Digital Signature - Directly below reg number, Only for PDF download */}
                  {digitalSignature && (
                    <div className="mt-1 pdf-only print:hidden" style={{ display: 'none' }}>
                      <img 
                        src={digitalSignature.startsWith('http') ? digitalSignature : `${process.env.REACT_APP_BACKEND_URL}${digitalSignature}`}
                        alt="Doctor Signature" 
                        className="h-16 w-32 ml-auto object-contain"
                        onError={(e) => {
                          console.error('Failed to load digital signature:', digitalSignature);
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Additional Tests */}
          {session.additional_tests && (
            <div className="mb-4">
              <div className="text-base font-semibold mb-1 section-header">Additional Tests:</div>
              <div className="text-base font-medium">{session.additional_tests}</div>
            </div>
          )}

          {/* Advice */}
          {session.notes && (
            <div className="mb-4">
              <div className="text-base font-semibold mb-1">Advice:</div>
              <div className="text-base font-medium">{session.notes}</div>
            </div>
          )}

          {/* Next Follow-up */}
          {session.next_followup && (
            <div className="mb-4">
              <div className="text-base font-semibold mb-1">Next Follow-up:</div>
              <div className="text-base font-medium">
                {(() => {
                  // Convert YYYY-MM-DD to DD/MM/YYYY
                  const date = new Date(session.next_followup);
                  const day = String(date.getDate()).padStart(2, '0');
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const year = date.getFullYear();
                  return `${day}/${month}/${year}`;
                })()}
              </div>
            </div>
          )}
        </div>

        {/* Print and Download Buttons */}
        <div className="mt-6 text-center no-print flex gap-4 justify-center">
          <button
            onClick={() => window.print()}
            className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-semibold"
          >
            Print Prescription
          </button>
          <button
            onClick={async () => {
              // Format date as DDMMYYYY
              const formatDateDDMMYYYY = (dateStr) => {
                if (!dateStr) return '';
                let date;
                if (dateStr.includes('/')) {
                  const [day, month, year] = dateStr.split('/');
                  return `${day}${month}${year}`;
                } else if (dateStr.includes('-')) {
                  date = new Date(dateStr);
                  const day = String(date.getDate()).padStart(2, '0');
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const year = date.getFullYear();
                  return `${day}${month}${year}`;
                }
                return '';
              };
              
              // Build filename: Name(ID) Date HospitalName
              const patientName = baby?.name || 'Patient';
              const patientId = baby?.short_id || baby?.id?.substring(0, 8).toUpperCase() || 'XXXXXXXX';
              const sessionDate = formatDateDDMMYYYY(session?.session_date);
              const hospitalName = session?.hospital_name || 'Hospital';
              
              // Format: Ravi Raj(AKARRAKR) 24092024 Foot Doctor.pdf
              const filename = `${patientName}(${patientId}) ${sessionDate} ${hospitalName}.pdf`;
              
              setDownloading(true);
              
              try {
                const element = prescriptionRef.current;
                
                // Preload all images before generating PDF
                const images = element.querySelectorAll('img');
                const imageLoadPromises = Array.from(images).map(img => {
                  return new Promise((resolve, reject) => {
                    if (img.complete) {
                      resolve();
                    } else {
                      img.onload = resolve;
                      img.onerror = resolve; // Continue even if image fails
                      // Force reload if not loaded
                      const src = img.src;
                      img.src = '';
                      img.src = src;
                    }
                  });
                });
                
                // Wait for all images to load
                await Promise.all(imageLoadPromises);
                
                // Hide buttons for PDF generation (like print preview)
                const noPrintElements = element.querySelectorAll('.no-print');
                noPrintElements.forEach(el => el.style.display = 'none');
                
                // Hide hospital branding in PDF if setting is off
                const hospitalBrandingSection = element.querySelector('.hospital-branding-section');
                const originalHospitalBrandingDisplay = hospitalBrandingSection ? hospitalBrandingSection.style.display : '';
                if (!showHospitalBrandingInPdf && hospitalBrandingSection) {
                  hospitalBrandingSection.style.display = 'none';
                }
                
                // Temporarily show digital signature for PDF generation
                const pdfOnlyElements = element.querySelectorAll('.pdf-only');
                pdfOnlyElements.forEach(el => el.style.display = 'block');
                
                // FAST PATH: If there's a prescription background PDF, use optimized direct approach
                if (prescriptionBackground) {
                  try {
                    // Dynamically import pdf-lib
                    const { PDFDocument } = await import('pdf-lib');
                    
                    // Fetch the background PDF directly (faster than converting to image)
                    const backgroundUrl = prescriptionBackground.startsWith('http') 
                      ? prescriptionBackground 
                      : `${process.env.REACT_APP_BACKEND_URL}${prescriptionBackground}`;
                    
                    const backgroundPdfResponse = await fetch(backgroundUrl);
                    const backgroundPdfBytes = await backgroundPdfResponse.arrayBuffer();
                    const backgroundPdfDoc = await PDFDocument.load(backgroundPdfBytes);
                    
                    // Import html2canvas for transparent rendering
                    const html2canvas = (await import('html2canvas')).default;
                    
                    // Remove background AND box-shadow from elements before capturing
                    const originalStyles = new Map();
                    const allElements = [element, ...element.querySelectorAll('*')];
                    allElements.forEach(el => {
                      const computedStyle = window.getComputedStyle(el);
                      originalStyles.set(el, {
                        bg: el.style.backgroundColor,
                        bgImage: el.style.backgroundImage,
                        boxShadow: el.style.boxShadow,
                        className: el.className,
                      });
                      // Force transparent on ALL elements
                      el.style.setProperty('background-color', 'transparent', 'important');
                      el.style.setProperty('background-image', 'none', 'important');
                      el.style.setProperty('background', 'transparent', 'important');
                      // Remove box-shadow (causes gray artifacts)
                      el.style.setProperty('box-shadow', 'none', 'important');
                      // Remove Tailwind bg-*, shadow-*, and gray-* classes
                      if (el.className && typeof el.className === 'string') {
                        el.className = el.className
                          .replace(/bg-\S+/g, '')
                          .replace(/shadow-\S+/g, '')
                          .replace(/gray-\S+/g, '');
                      }
                    });
                    
                    // Use html2canvas to render content with transparent background
                    // IMPORTANT: Use rgba(0,0,0,0) instead of null to avoid gray background bug
                    const contentCanvas = await html2canvas(element, {
                      scale: 2,  // High resolution for crisp text
                      useCORS: true,
                      allowTaint: true,
                      backgroundColor: 'rgba(0,0,0,0)',  // Transparent background
                      logging: false,
                      removeContainer: true,
                      letterRendering: true,  // Better text rendering
                    });
                    
                    // DEBUG: Check if canvas has transparency
                    console.log('Canvas size:', contentCanvas.width, 'x', contentCanvas.height);
                    
                    // Restore original styles
                    allElements.forEach(el => {
                      const orig = originalStyles.get(el);
                      if (orig) {
                        el.style.removeProperty('background-color');
                        el.style.removeProperty('background-image');
                        el.style.removeProperty('background');
                        el.style.removeProperty('box-shadow');
                        el.style.backgroundColor = orig.bg;
                        el.style.backgroundImage = orig.bgImage;
                        el.style.boxShadow = orig.boxShadow;
                        if (orig.className) el.className = orig.className;
                      }
                    });
                    
                    // Convert canvas to PNG bytes
                    const contentPngBytes = await new Promise(resolve => {
                      contentCanvas.toBlob(blob => blob.arrayBuffer().then(resolve), 'image/png');
                    });
                    
                    // Create merged PDF
                    const mergedPdfDoc = await PDFDocument.create();
                    const contentImage = await mergedPdfDoc.embedPng(contentPngBytes);
                    
                    // A4 dimensions in points
                    const A4_WIDTH = 595.28;
                    const A4_HEIGHT = 841.89;
                    
                    // Calculate content dimensions
                    const contentAspectRatio = contentCanvas.width / contentCanvas.height;
                    const scaledContentHeight = A4_WIDTH / contentAspectRatio;
                    const numPages = Math.ceil(scaledContentHeight / A4_HEIGHT);
                    
                    // Embed background page once - get the first page from the background PDF
                    const bgPages = backgroundPdfDoc.getPages();
                    console.log('Background PDF has', bgPages.length, 'pages');
                    
                    if (bgPages.length === 0) {
                      throw new Error('Background PDF has no pages');
                    }
                    
                    const [embeddedBg] = await mergedPdfDoc.embedPages([bgPages[0]]);
                    console.log('Background embedded successfully');
                    
                    for (let i = 0; i < Math.max(1, numPages); i++) {
                      const newPage = mergedPdfDoc.addPage([A4_WIDTH, A4_HEIGHT]);
                      
                      // Draw background (bottom layer)
                      newPage.drawPage(embeddedBg, { x: 0, y: 0, width: A4_WIDTH, height: A4_HEIGHT });
                      
                      // Draw content on top
                      const yOffset = i * A4_HEIGHT;
                      newPage.drawImage(contentImage, {
                        x: 0,
                        y: A4_HEIGHT - scaledContentHeight + yOffset,
                        width: A4_WIDTH,
                        height: scaledContentHeight,
                      });
                    }
                    
                    // Save and download
                    const mergedPdfBytes = await mergedPdfDoc.save();
                    const blob = new Blob([mergedPdfBytes], { type: 'application/pdf' });
                    const link = document.createElement('a');
                    link.href = URL.createObjectURL(blob);
                    link.download = filename;
                    link.click();
                    URL.revokeObjectURL(link.href);
                    
                    // Restore visibility
                    noPrintElements.forEach(el => el.style.display = '');
                    pdfOnlyElements.forEach(el => el.style.display = '');
                    if (hospitalBrandingSection) {
                      hospitalBrandingSection.style.display = originalHospitalBrandingDisplay;
                    }
                    setDownloading(false);
                    return;
                  } catch (bgError) {
                    console.error('Background PDF merge failed, falling back to standard:', bgError);
                    // Fall through to standard method
                  }
                }
                
                // STANDARD PATH: No background PDF, use html2pdf normally
                const opt = {
                  margin: [20, 8, 20, 8],  // [top, right, bottom, left] in mm - space for header on pages 2+ and footer
                  filename: filename,
                  image: { type: 'jpeg', quality: 0.95 },
                  html2canvas: { 
                    scale: 2,
                    useCORS: true,
                    allowTaint: true,
                    logging: false,
                    letterRendering: true,
                    scrollY: 0,
                    scrollX: 0,
                    windowHeight: document.documentElement.scrollHeight,
                    imageTimeout: 15000,
                    backgroundColor: '#ffffff',
                    onclone: function(clonedDoc) {
                      const images = clonedDoc.querySelectorAll('img');
                      images.forEach(img => {
                        img.style.display = 'inline-block';
                        img.style.visibility = 'visible';
                        img.style.opacity = '1';
                      });
                    }
                  },
                  jsPDF: { 
                    unit: 'mm', 
                    format: 'a4', 
                    orientation: 'portrait'
                  },
                  pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
                  enableLinks: false
                };
                
                // Generate PDF with custom header and footer
                const worker = html2pdf().set(opt).from(element);
                
                // Get the jsPDF instance to add headers and footers
                const pdf = await worker.toPdf().get('pdf');
                
                const pageCount = pdf.internal.getNumberOfPages();
                const pageHeight = pdf.internal.pageSize.getHeight();
                const pageWidth = pdf.internal.pageSize.getWidth();
                
                // Helper function to load image
                function loadImage(url) {
                  return new Promise((resolve, reject) => {
                    const img = new Image();
                    img.crossOrigin = 'anonymous';
                    img.onload = () => resolve(img);
                    img.onerror = reject;
                    img.src = url;
                  });
                }
                
                // Load hospital logo as image for header and footer
                let hospitalLogoImg = null;
                if (session.hospital_logo) {
                  try {
                    const logoUrl = session.hospital_logo.startsWith('http') 
                      ? session.hospital_logo 
                      : `${process.env.REACT_APP_BACKEND_URL}${session.hospital_logo}`;
                    hospitalLogoImg = await loadImage(logoUrl);
                  } catch (err) {
                    console.error('Failed to load hospital logo for PDF:', err);
                  }
                }
                
                // Add header and footer to each page
                for (let i = 1; i <= pageCount; i++) {
                  pdf.setPage(i);
                  
                  // Header on every page (except first page which already has it)
                  if (i > 1) {
                    let yPosition = 5; // Start position for header at top
                    
                    // Hospital logo and info on left, Doctor info on right
                    if (hospitalLogoImg && session.hospital_name) {
                      // Hospital logo (smaller for repeated headers)
                      pdf.addImage(hospitalLogoImg, 'PNG', 8, yPosition, 10, 10);
                      
                      // Hospital name
                      pdf.setFontSize(9);
                      pdf.setTextColor(50, 50, 50);
                      pdf.setFont(undefined, 'bold');
                      pdf.text(session.hospital_name, 20, yPosition + 5);
                      
                      // Hospital address (if available)
                      if (session.hospital_address) {
                        pdf.setFontSize(6);
                        pdf.setTextColor(100, 100, 100);
                        pdf.setFont(undefined, 'normal');
                        // Split address if too long
                        const addressLines = pdf.splitTextToSize(session.hospital_address, 80);
                        pdf.text(addressLines, 20, yPosition + 8);
                      }
                    }
                    
                    // Doctor info on right
                    pdf.setFontSize(7);
                    pdf.setTextColor(50, 50, 50);
                    pdf.setFont(undefined, 'normal');
                    
                    // Parse doctor name
                    const doctorName = session.doctor_name || '';
                    const specializationMatch = doctorName.match(/\(([^)]+)\)$/);
                    const name = specializationMatch ? doctorName.replace(/\s*\([^)]+\)$/, '').trim() : doctorName;
                    const specialization = specializationMatch ? specializationMatch[1] : '';
                    
                    const rightX = pageWidth - 8;
                    pdf.text(name, rightX, yPosition + 4, { align: 'right' });
                    
                    if (specialization) {
                      pdf.setTextColor(100, 100, 100);
                      pdf.text(specialization, rightX, yPosition + 7, { align: 'right' });
                    }
                    
                    if (session.doctor_registration) {
                      pdf.setFontSize(6);
                      pdf.text(`(Reg No: ${session.doctor_registration})`, rightX, yPosition + 10, { align: 'right' });
                    }
                    
                    // Line separator below header
                    pdf.setDrawColor(200, 200, 200);
                    pdf.setLineWidth(0.3);
                    pdf.line(8, yPosition + 13, pageWidth - 8, yPosition + 13);
                  }
                  
                  // Footer - Bottom left with hospital logo
                  if (hospitalLogoImg) {
                    const logoSize = 8; // 8mm square
                    pdf.addImage(hospitalLogoImg, 'PNG', 8, pageHeight - 15, logoSize, logoSize);
                  }
                  
                  // Page number - Bottom right
                  pdf.setFontSize(8);
                  pdf.setTextColor(150, 150, 150);
                  pdf.text(`Page ${i} of ${pageCount}`, pageWidth - 8, pageHeight - 8, { align: 'right' });
                }
                
                // Save the PDF (standard path - no background)
                pdf.save(filename);
                
                // Restore button visibility and hide digital signature again
                noPrintElements.forEach(el => el.style.display = '');
                pdfOnlyElements.forEach(el => el.style.display = 'none');
                if (hospitalBrandingSection) {
                  hospitalBrandingSection.style.display = originalHospitalBrandingDisplay;
                }
              } catch (error) {
                console.error('Error generating PDF:', error);
                alert('Failed to generate PDF. Please try again.');
              } finally {
                setDownloading(false);
              }
            }}
            disabled={downloading}
            className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2"
          >
            {downloading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Generating PDF...</span>
              </>
            ) : (
              'Download as PDF'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrescriptionView;
